/**
 * Shared category vocabulary (label + colour) for run-of-show programme
 * items — `tasks` rows with `task_type='run_of_show'`, coloured by
 * `owner_team`.
 *
 * Single source of truth for the Day Schedule tab, the Event Day Timeline
 * (Gantt + Run of Show) and anything else that renders a programme item. Add
 * a category here once and every surface picks it up — do not re-declare a
 * local colour or label map in a component.
 *
 * The list matches TasksService.TEAM_OPTIONS (the same `owner_team` roster
 * used by ordinary planning tasks) plus 'other' for items with no team set.
 */

export interface ScheduleCategoryDef {
  value: string;
  label: string;
  color: string;
}

export const SCHEDULE_CATEGORIES = [
  { value: 'aunties',             label: 'Aunties',             color: '#c2185b' },
  { value: 'branding',            label: 'Branding',            color: '#5d4037' },
  { value: 'couple',              label: 'Couple',              color: '#ad1457' },
  { value: 'decorator',           label: 'Decorator',           color: '#00838f' },
  { value: 'digital',             label: 'Digital',             color: '#0277bd' },
  { value: 'entertainment',       label: 'Entertainment',       color: '#bf360c' },
  { value: 'extra curricular',    label: 'Extra Curricular',    color: '#6a1b9a' },
  { value: 'food + drinks',       label: 'Food & Drinks',       color: '#f9a825' },
  { value: 'groomsparty',         label: "Groom's Party",       color: '#1565c0' },
  { value: 'initators',           label: 'Initiators',          color: '#00695c' },
  { value: 'internal logistics',  label: 'Internal Logistics',  color: '#546e7a' },
  { value: 'inventory',           label: 'Inventory',           color: '#795548' },
  { value: 'media',               label: 'Media',                color: '#8e24aa' },
  { value: 'planner',             label: 'Planner',             color: '#4527a0' },
  { value: 'security',            label: 'Security',            color: '#37474f' },
  { value: 'other',               label: 'Other',               color: '#78909c' },
] as const satisfies readonly ScheduleCategoryDef[];

export type ScheduleCategory = typeof SCHEDULE_CATEGORIES[number]['value'];

export const FALLBACK_SCHEDULE_CATEGORY: ScheduleCategory = 'other';

const BY_VALUE = new Map<string, ScheduleCategoryDef>(
  SCHEDULE_CATEGORIES.map(c => [c.value, c])
);

/** Legacy / free-text spellings that map onto a canonical category. */
const ALIASES: Record<string, ScheduleCategory> = {
  general: 'other',
  '': 'other',
  photography: 'media',
  photo: 'media',
  'photo/video': 'media',
  food: 'food + drinks',
  drinks: 'food + drinks',
  catering: 'food + drinks',
  ceremony: 'couple',
  logistics: 'internal logistics',
  vendor: 'other',
  reception: 'other',
  break: 'other',
};

/**
 * Coerces an arbitrary API/DB category string to a known category.
 * Unknown values fall back to 'other' rather than being cast blindly.
 */
export function normalizeScheduleCategory(raw: unknown): ScheduleCategory {
  if (typeof raw !== 'string') return FALLBACK_SCHEDULE_CATEGORY;
  const key = raw.trim().toLowerCase();
  if (BY_VALUE.has(key)) return key as ScheduleCategory;
  return ALIASES[key] ?? FALLBACK_SCHEDULE_CATEGORY;
}

export function scheduleCategoryLabel(category: string): string {
  return BY_VALUE.get(normalizeScheduleCategory(category))!.label;
}

export function scheduleCategoryColor(category: string): string {
  return BY_VALUE.get(normalizeScheduleCategory(category))!.color;
}

/** Categories that matter most when triaging the day. */
export const HIGH_PRIORITY_SCHEDULE_CATEGORIES: ReadonlySet<string> = new Set<string>([
  'couple',
  'food + drinks',
  'security',
  'internal logistics',
]);
